import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Add } from "@carbon/icons-react";
import {
  Button,
  ComposedModal,
  DismissibleTag,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  TextInput,
  Toggle,
} from "@carbon/react";
import { CERTIFICATIONS, certificationSuggestions } from "../../utils";
import { CertificationRequest, CertificationsEnum } from "../../types";

export interface MaterialCertificationsProps {
  certifications: CertificationRequest[];
  onChange: (certifications: CertificationRequest[]) => void;
}

type CertificationOption = { type: CertificationsEnum; label: string };

/**
 * The certifications add/list widget — mirrors the tag-picker UX (`components/carbon/TagPicker`):
 * a list of removable chips plus a "New certification" button that opens a Carbon `ComposedModal`
 * to create one, replacing the pre-Carbon inline expandable form. Unlike tags there is no
 * company-wide catalogue to pick from — each certification is authored per material (a
 * `CertificationsEnum` type + a free-text name + a `required` flag) — so this is create-only, held
 * in local state and handed to the parent via `onChange`, exactly as before. No RHF/Yup binding is
 * introduced (there never was one; the only gate is type + name both present).
 */
const MaterialCertifications = ({
  certifications,
  onChange,
}: MaterialCertificationsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<CertificationOption | null>(null);
  const [name, setName] = useState("");
  const [required, setRequired] = useState(false);

  const closeDialog = () => {
    setType(null);
    setName("");
    setRequired(false);
    setOpen(false);
  };

  const handleAdd = () => {
    if (!type || !name) return;
    onChange([
      ...certifications,
      { name, type: type.type, label: type.label, required },
    ]);
    closeDialog();
  };

  const handleRemove = (index: number) => {
    onChange(certifications.filter((_, i) => i !== index));
  };

  return (
    <div className="material-certifications">
      <p className="material-certifications__title">
        {t("certifications.certifications")}
      </p>

      <Button
        kind="tertiary"
        renderIcon={Add}
        onClick={() => setOpen(true)}
        className="material-certifications__add"
      >
        {t("certifications.addCertifications")}
      </Button>

      {certifications.length > 0 && (
        <ul className="material-certifications__list">
          {certifications.map((certification, index) => (
            <li key={`${certification.name}-${index}`}>
              <DismissibleTag
                type="blue"
                size="md"
                text={`${certification.name} (${certification.label})${
                  certification.required ? " *" : ""
                }`}
                onClose={() => handleRemove(index)}
              />
            </li>
          ))}
        </ul>
      )}

      <p className="material-certifications__hint">
        {t("certifications.hint", {
          defaultValue:
            "Add the quality or compliance certifications this material requires.",
        })}
      </p>

      <ComposedModal open={open} onClose={closeDialog} size="sm">
        <ModalHeader title={t("certifications.addCertifications")} />
        <ModalBody hasForm>
          <Select
            id="certificationType"
            labelText={t("certifications.certificationType")}
            value={type?.type ?? ""}
            onChange={(event) => {
              const key = event.target.value as keyof typeof CERTIFICATIONS;
              setType(key ? (CERTIFICATIONS[key] as CertificationOption) : null);
            }}
          >
            <SelectItem
              value=""
              text={t("certifications.selectCertificationType")}
            />
            {Object.keys(CERTIFICATIONS).map((key) => (
              <SelectItem
                key={key}
                value={CERTIFICATIONS[key as keyof typeof CERTIFICATIONS].type}
                text={CERTIFICATIONS[key as keyof typeof CERTIFICATIONS].label}
              />
            ))}
          </Select>

          {type && (
            <div className="material-certifications__fields">
              <TextInput
                id="certificationName"
                labelText={t("certifications.certificationName")}
                placeholder={t("certifications.insertCertificationName")}
                value={name}
                onChange={(event) => setName(event.target.value)}
                list="certification-suggestions"
              />
              <datalist id="certification-suggestions">
                {certificationSuggestions[type.type]?.map((suggestion) => (
                  <option key={suggestion} value={suggestion} />
                ))}
              </datalist>

              <Toggle
                id="certificationRequired"
                labelText={t("certifications.certificationRequired")}
                toggled={required}
                onToggle={setRequired}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter
          secondaryButtonText={t("cta.cancel")}
          primaryButtonText={t("cta.add")}
          primaryButtonDisabled={!type || !name}
          onRequestSubmit={handleAdd}
        >
          {null}
        </ModalFooter>
      </ComposedModal>
    </div>
  );
};

export default MaterialCertifications;
